import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Setup.css';

const STEPS = ['welcome', 'database', 'admin', 'data'];
const STEP_LABELS = ['Bienvenue', 'Base de donnees', 'Administrateur', 'Donnees'];

const CheckIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const Spinner = ({ size = 16 }) => (
  <svg className="setup-spinner" width={size} height={size} fill="none" viewBox="0 0 24 24">
    <circle opacity="0.25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path opacity="0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const STEP_ICONS = [
  <svg key="w" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" /></svg>,
  <svg key="d" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 3.6 3 8 3s8-1 8-3V7M4 7c0 2 3.6 3 8 3s8-1 8-3M4 7c0-2 3.6-3 8-3s8 1 8 3" /></svg>,
  <svg key="a" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
  <svg key="dt" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
];

export default function Setup() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const [dbConfig, setDbConfig] = useState({ host: 'localhost', port: '27017', dbName: 'TicketFlow_dev', username: '', password: '' });
  const [dbTestResult, setDbTestResult] = useState(null);
  const [dbTesting, setDbTesting] = useState(false);

  const [adminConfig, setAdminConfig] = useState({ username: '', fullName: '', email: '', password: '', confirmPassword: '' });

  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('empty');
  const [createDemoUsers, setCreateDemoUsers] = useState(true);
  const [loadingDatasets, setLoadingDatasets] = useState(false);

  const [installing, setInstalling] = useState(false);
  const [installResult, setInstallResult] = useState(null);

  useEffect(() => {
    fetch('/api/setup/status').then(r => r.json()).then(d => { if (!d.needsSetup) navigate('/login'); }).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (currentStep === 3 && datasets.length === 0) {
      setLoadingDatasets(true);
      fetch('/api/setup/datasets').then(r => r.json()).then(d => { setDatasets(d.datasets || []); setLoadingDatasets(false); }).catch(() => setLoadingDatasets(false));
    }
  }, [currentStep, datasets.length]);

  const updateDb = (f, v) => { setDbConfig(p => ({ ...p, [f]: v })); setDbTestResult(null); };

  const testDb = async () => {
    setDbTesting(true); setDbTestResult(null);
    try {
      const r = await fetch('/api/setup/test-db', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dbConfig) });
      setDbTestResult(await r.json());
    } catch { setDbTestResult({ success: false, error: 'Impossible de contacter le serveur' }); }
    setDbTesting(false);
  };

  const canGoNext = () => {
    if (STEPS[currentStep] === 'welcome') return true;
    if (STEPS[currentStep] === 'database') return dbTestResult?.success === true;
    if (STEPS[currentStep] === 'admin') {
      const { username, fullName, password, confirmPassword } = adminConfig;
      return username.length >= 3 && fullName.length >= 2 && password.length >= 6 && password === confirmPassword;
    }
    return false;
  };

  const runInstall = async () => {
    setInstalling(true); setInstallResult(null);
    try {
      const r = await fetch('/api/setup/install', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          db: dbConfig,
          admin: { username: adminConfig.username, fullName: adminConfig.fullName, email: adminConfig.email || undefined, password: adminConfig.password },
          dataset: selectedDataset,
          createDemoUsers: selectedDataset !== 'empty' && createDemoUsers
        })
      });
      setInstallResult(await r.json());
    } catch { setInstallResult({ success: false, error: 'Erreur de communication avec le serveur' }); }
    setInstalling(false);
  };

  // ── Stepper ──
  const renderStepper = () => (
    <div className="setup-stepper">
      {STEPS.map((_, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className={`setup-step-connector ${i <= currentStep ? 'active' : 'inactive'}`} />}
          <div className="setup-step-item">
            <div className={`setup-step-circle ${i < currentStep ? 'done' : i === currentStep ? 'current' : 'pending'}`}>
              {i < currentStep ? <CheckIcon /> : STEP_ICONS[i]}
            </div>
            <span className={`setup-step-label ${i === currentStep ? 'current' : 'inactive'}`}>{STEP_LABELS[i]}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  // ── Step 1 ──
  const renderWelcome = () => (
    <div style={{ textAlign: 'center', padding: '2rem 0' }}>
      <div className="setup-welcome-icon">
        <svg width="56" height="56" fill="none" stroke="#2563eb" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
        </svg>
      </div>
      <div className="setup-welcome-title">Bienvenue dans TicketFlow</div>
      <div className="setup-welcome-subtitle">Assistant d'installation et de configuration</div>
      <div className="setup-welcome-desc">
        Cet assistant va vous guider pour configurer votre base de donnees,
        creer un compte administrateur et charger un jeu de donnees initial pour votre agence.
      </div>
      <div className="setup-welcome-features">
        <span>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 3.6 3 8 3s8-1 8-3V7" /></svg>
          Base de donnees
        </span>
        <span>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
          Administrateur
        </span>
        <span>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5" /></svg>
          Configuration
        </span>
      </div>
    </div>
  );

  // ── Step 2 ──
  const renderDatabase = () => (
    <div className="setup-spacer">
      <div>
        <div className="setup-section-title">Configuration de la base de donnees</div>
        <div className="setup-section-desc">Configurez la connexion MongoDB pour TicketFlow</div>
      </div>
      <div className="setup-grid-2">
        <div>
          <label>Hote</label>
          <input type="text" value={dbConfig.host} onChange={e => updateDb('host', e.target.value)} placeholder="localhost" />
        </div>
        <div>
          <label>Port</label>
          <input type="text" value={dbConfig.port} onChange={e => updateDb('port', e.target.value)} placeholder="27017" />
        </div>
      </div>
      <div>
        <label>Nom de la base</label>
        <input type="text" value={dbConfig.dbName} onChange={e => updateDb('dbName', e.target.value)} placeholder="TicketFlow" />
      </div>
      <div className="setup-grid-2">
        <div>
          <label>Utilisateur <span className="label-optional">(optionnel)</span></label>
          <input type="text" value={dbConfig.username} onChange={e => updateDb('username', e.target.value)} placeholder="devuser" />
        </div>
        <div>
          <label>Mot de passe <span className="label-optional">(optionnel)</span></label>
          <input type="password" value={dbConfig.password} onChange={e => updateDb('password', e.target.value)} placeholder="••••••" />
        </div>
      </div>
      <div className="setup-test-row">
        <button className="setup-btn-dark" onClick={testDb} disabled={dbTesting}>
          {dbTesting ? <><Spinner /> Test en cours...</> : 'Tester la connexion'}
        </button>
        {dbTestResult && (
          <div className={`setup-test-result ${dbTestResult.success ? 'success' : 'error'}`}>
            {dbTestResult.success ? (
              <><svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Connexion OK — MongoDB v{dbTestResult.version}</>
            ) : (
              <><svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> {dbTestResult.error}</>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ── Step 3 ──
  const renderAdmin = () => {
    const mismatch = adminConfig.password && adminConfig.confirmPassword && adminConfig.password !== adminConfig.confirmPassword;
    return (
      <div className="setup-spacer">
        <div>
          <div className="setup-section-title">Creer le compte administrateur</div>
          <div className="setup-section-desc">Ce compte aura tous les droits sur l'application</div>
        </div>
        <div className="setup-grid-2">
          <div>
            <label>Nom d'utilisateur *</label>
            <input type="text" value={adminConfig.username} onChange={e => setAdminConfig(p => ({ ...p, username: e.target.value }))} placeholder="admin" />
            {adminConfig.username && adminConfig.username.length < 3 && <p className="setup-hint warning">Minimum 3 caracteres</p>}
          </div>
          <div>
            <label>Nom complet *</label>
            <input type="text" value={adminConfig.fullName} onChange={e => setAdminConfig(p => ({ ...p, fullName: e.target.value }))} placeholder="Administrateur Principal" />
          </div>
        </div>
        <div>
          <label>Email <span className="label-optional">(optionnel)</span></label>
          <input type="email" value={adminConfig.email} onChange={e => setAdminConfig(p => ({ ...p, email: e.target.value }))} placeholder="admin@ticketflow.dz" />
        </div>
        <div className="setup-grid-2">
          <div>
            <label>Mot de passe *</label>
            <input type="password" value={adminConfig.password} onChange={e => setAdminConfig(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" />
            {adminConfig.password && adminConfig.password.length < 6 && <p className="setup-hint warning">Minimum 6 caracteres</p>}
          </div>
          <div>
            <label>Confirmer *</label>
            <input type="password" className={mismatch ? 'input-error' : ''} value={adminConfig.confirmPassword} onChange={e => setAdminConfig(p => ({ ...p, confirmPassword: e.target.value }))} placeholder="••••••••" />
            {mismatch && <p className="setup-hint error">Les mots de passe ne correspondent pas</p>}
          </div>
        </div>
      </div>
    );
  };

  // ── Step 4 ──
  const renderData = () => (
    <div className="setup-spacer">
      <div>
        <div className="setup-section-title">Donnees initiales</div>
        <div className="setup-section-desc">Choisissez un modele d'agence ou commencez avec une installation vide</div>
      </div>

      <div className="setup-dataset-list">
        <label className={`setup-dataset-item ${selectedDataset === 'empty' ? 'selected' : ''}`}>
          <div className="setup-dataset-row">
            <input type="radio" name="dataset" checked={selectedDataset === 'empty'} onChange={() => setSelectedDataset('empty')} />
            <div className="setup-dataset-info">
              <div className="setup-dataset-name">Installation vide</div>
              <div className="setup-dataset-desc">Aucun service ni guichet pre-configure.</div>
            </div>
          </div>
        </label>

        {loadingDatasets ? (
          <div className="setup-loading"><Spinner /> Chargement des modeles...</div>
        ) : datasets.map(ds => (
          <label key={ds.filename} className={`setup-dataset-item ${selectedDataset === ds.filename ? 'selected' : ''}`}>
            <div className="setup-dataset-row">
              <input type="radio" name="dataset" checked={selectedDataset === ds.filename} onChange={() => setSelectedDataset(ds.filename)} />
              <div className="setup-dataset-info">
                <div>
                  <span className="setup-dataset-name">{ds.name}</span>
                  {ds.nameAr && <span className="setup-dataset-name-ar" dir="rtl">{ds.nameAr}</span>}
                </div>
                {ds.description && <div className="setup-dataset-desc">{ds.description}</div>}
                <div className="setup-dataset-badges">
                  <span className="setup-badge blue">{ds.servicesCount} services</span>
                  <span className="setup-badge green">{ds.countersCount} guichets</span>
                </div>
                {ds.services?.length > 0 && (
                  <div className="setup-dataset-services">{ds.services.map(s => s.name).join(' · ')}</div>
                )}
              </div>
            </div>
          </label>
        ))}
      </div>

      {selectedDataset !== 'empty' && (
        <label className="setup-checkbox-row">
          <input type="checkbox" checked={createDemoUsers} onChange={e => setCreateDemoUsers(e.target.checked)} disabled={installing || installResult?.success} />
          Creer des utilisateurs de demo (1 agent par guichet + 1 manager)
        </label>
      )}

      <div className="setup-summary">
        <div className="setup-summary-title">Resume de l'installation</div>
        <div className="setup-grid-3">
          <div className="setup-summary-card">
            <div className="setup-summary-label">Base de donnees</div>
            <div className="setup-summary-value">{dbConfig.host}:{dbConfig.port}/{dbConfig.dbName}</div>
          </div>
          <div className="setup-summary-card">
            <div className="setup-summary-label">Administrateur</div>
            <div className="setup-summary-value">{adminConfig.username || '—'}</div>
          </div>
          <div className="setup-summary-card">
            <div className="setup-summary-label">Modele</div>
            <div className="setup-summary-value">{selectedDataset === 'empty' ? 'Vide' : datasets.find(d => d.filename === selectedDataset)?.name || selectedDataset}</div>
          </div>
        </div>
      </div>

      {installResult && (
        <div className={`setup-result ${installResult.success ? 'success' : 'error'}`}>
          {installResult.success ? (
            <div>
              <div className="setup-result-header">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Installation reussie !
              </div>
              {installResult.steps && (
                <ul className="setup-result-steps">{installResult.steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
              )}
            </div>
          ) : (
            <div className="setup-result-error-row">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {installResult.error}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {installResult?.success ? (
          <button className="setup-btn-success setup-btn-install" onClick={() => navigate('/login')}>Aller a la connexion</button>
        ) : (
          <button className="setup-btn-primary setup-btn-install" onClick={runInstall} disabled={installing}>
            {installing ? <><Spinner /> Installation en cours...</> : 'Installer'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="setup-wizard">
      <div className="setup-card">
        {renderStepper()}
        <div className="setup-content">
          {currentStep === 0 && renderWelcome()}
          {currentStep === 1 && renderDatabase()}
          {currentStep === 2 && renderAdmin()}
          {currentStep === 3 && renderData()}
        </div>
        {currentStep < 3 && (
          <div className="setup-footer">
            <button className="setup-btn-secondary" onClick={() => setCurrentStep(p => p - 1)} disabled={currentStep === 0}>Precedent</button>
            <button className="setup-btn-primary" onClick={() => setCurrentStep(p => p + 1)} disabled={!canGoNext()}>Suivant</button>
          </div>
        )}
        {currentStep === 3 && !installResult?.success && (
          <div className="setup-footer">
            <button className="setup-btn-secondary" onClick={() => setCurrentStep(p => p - 1)} disabled={installing}>Precedent</button>
          </div>
        )}
      </div>
    </div>
  );
}
